import { ECardType } from '../utils/config';

export const PAY_FRAME_NAME = 'pay';

export type TFieldOptions = {
  required: boolean;
  label?: string;
  value?: string;
  visible?: boolean;
};

export type TPaymentsMethod = {
  guid: string;
  description: string;
  accountType: string;
  lastFour: string;
  aba: string;
  expiration: string;
  firstName: string;
  lastName: string;
  country: string;
};

export type TClientFeesDisclaimers = {
  disclaimer: string;
  paymentType: string;
  accountBrand?: string;
};

export type TPayPageFeesDisclaimers = {
  feeType: string;
  key: string;
  paymentType?: string;
  data: Record<string, string>;
};

export const TranActions = {
  AUTH: 'Auth',
  SALE: 'Sale',
  AVS: 'Avs',
  VERIFY: 'Verify',
} as const;

export type TranActionType = (typeof TranActions)[keyof typeof TranActions];

export type EncryptionData = {
  key: string;
  keyId: string;
};

export interface IConfig {
  amount: string;
  currencyCode: string;
  clientFeesDisclaimers: Array<TClientFeesDisclaimers>;
  feesDisclaimers: Array<TPayPageFeesDisclaimers>;
  description?: string;
  approvalUrl: string;
  rejectedUrl: string;
  timeoutUrl: string;
  enableShipping: boolean;
  enableBilling: boolean;
  enableBillingSummary: boolean;
  enableSectionHeaders: boolean;
  encryption: EncryptionData;
  locale: string | null;
  style?: {
    applePayButtonType: string;
    googlePayButtonType: string;
    bodyBg: string;
    mainColor: string;
    titleColor: string;
    subTitleColor: string;
    labelRadioColor: string;
    paymentMethodHeaderColor: string;
    paymentMethodMainColor: string;
    logoBorderColor: string;
    formBg: string;
    formBorder: string;
    formLabelColor: string;
    formColor: string;
    formPlaceholderColor: string;
    formBorderActive: string;
    formFocusVisibleColor: string;
    formErrorColor: string;
    buttonPrimaryBg: string;
    buttonPrimaryColor: string;
    buttonPrimaryHoverBg: string;
    buttonPrimaryHoverColor: string;
    buttonPrimaryActiveBg: string;
    buttonPrimaryActiveColor: string;
    buttonPrimaryDisableBg: string;
    buttonPrimaryDisableColor: string;
    buttonPrimaryFocusVisibleColor: string;
    buttonSecondaryBg: string;
    buttonSecondaryColor: string;
    buttonSecondaryHoverBg: string;
    buttonSecondaryHoverColor: string;
    buttonSecondaryActiveBg: string;
    buttonSecondaryActiveColor: string;
    buttonSecondaryDisableBg: string;
    buttonSecondaryDisableColor: string;
    buttonSecondaryFocusVisibleColor: string;
    billingBg: string;
    billingMainColor: string;
    billingSecondColor: string;
    billingBorderColor: string;
    billingBorderTotalColor: string;
    accountActiveBg: string;
    payWithAccountOnFileLabel: string;
    payWithCardLabel: string;
    payWithAchLabel: string;
    paywithFinancingLabel: string;
  };
  paymentMethod: {
    credit: string[];
    ach: boolean;
    googlePay: boolean;
    applePay: boolean;
    payPal: boolean;
    tuaPay: boolean;
  };
  billingAddress: {
    cvv?: TFieldOptions;
    firstName: TFieldOptions;
    lastName: TFieldOptions;
    email: TFieldOptions;
    country: TFieldOptions;
    region: TFieldOptions;
    city: TFieldOptions;
    postal: TFieldOptions;
    address: TFieldOptions;
    address2: TFieldOptions;
    phone: TFieldOptions;
  };
  shippingAddress: {
    firstName: TFieldOptions;
    lastName: TFieldOptions;
    email: TFieldOptions;
    country: TFieldOptions;
    region: TFieldOptions;
    city: TFieldOptions;
    postal: TFieldOptions;
    address: TFieldOptions;
    address2: TFieldOptions;
    phone: TFieldOptions;
  };
  industryData: {
    industryDataPurchaseId: TFieldOptions,
    industryDataIndustry: TFieldOptions,
    industryDataEci: TFieldOptions,
    industryDataMarketSpecificId: TFieldOptions,
    industryDataPartialApproval: TFieldOptions,
  },
  customFields: {
    id: string;
    required: boolean;
    label: string;
    value: string;
    visible: boolean;
  }[];
  message: string;
  code: string;
  info: string;
  rootCause?: string;
  createTime: string;
  expirationTime: string;
  tranAction: TranActionType;
  preffiledCardType: ECardType | null;
  tokenData: {
    paymentsMethod: TPaymentsMethod[];
    shipping: {
      guid: string;
      description: string;
      firstName: string;
      lastName: string;
      address: string;
      address2: string;
      city: string;
      postal: string;
      region: string;
      country: string;
      phone: string;
    };
  };
  softDescriptors?: {
    merchantName: string;
    merchantPhone: string;
    merchantState: string;
    description: string;
  } | null;
  storageSafe: {
    storageSafeAccountGuid: TFieldOptions;
    storageSafeGenerate: TFieldOptions;
    storageSafeGuid: TFieldOptions;
  }
}
